import { useState } from "react";
import Eyebrow from "@/components/base/Eyebrow";
import { brokerPartners, brokerRationales, brokerSelection } from "@/mocks/brokers";
import type { BrokerRationaleKey } from "@/mocks/brokers";

interface BrokerStripProps {
  label?: string;
  heading?: string;
  note?: string;
  rationaleKey?: BrokerRationaleKey;
  className?: string;
}

/**
 * Compact partner strip used inside long-form article pages.
 * Deliberately shallow: one row, no specs, no pitch paragraphs — it should read
 * as a footnote in the flow of the article, not as an interruption of it.
 * The "why these three" rationale lives behind a toggle so it never competes
 * with the article body until the reader asks for it.
 */
export default function BrokerStrip({
  label = "Where to trade it",
  heading = "When you are ready to place this order on a live account",
  note = "Order types are identical everywhere — pricing and platforms are not.",
  rationaleKey,
  className = "",
}: BrokerStripProps) {
  const [reasoningOpen, setReasoningOpen] = useState(false);
  const rationale = rationaleKey ? brokerRationales[rationaleKey] : undefined;
  const selection = rationale ?? brokerSelection;

  /* Card order follows the rationale order: index 0 is the closest fit for this
     page and renders first. Unknown names fall to the end rather than vanishing. */
  const rank = (name: string) => {
    const index = selection.reasons.findIndex((item) => item.name === name);
    return index === -1 ? selection.reasons.length : index;
  };
  const orderedPartners = [...brokerPartners].sort((a, b) => rank(a.name) - rank(b.name));

  return (
    <section
      className={`px-4 md:px-8 py-9 md:py-10 bg-background-200 border-y-2 border-foreground-950 ${className}`}
    >
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-12">
        <div className="lg:w-[34%] shrink-0">
          <Eyebrow tone="muted">{label}</Eyebrow>
          <p className="mt-3 font-heading font-bold uppercase tracking-tight leading-snug text-sm md:text-base text-foreground-950">
            {heading}
          </p>
          <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">{note}</p>
        </div>

        <ul className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2.5">
          {orderedPartners.map((broker, index) => {
            const isClosestFit = index === 0;
            return (
            <li key={broker.name} className="min-w-0">
              <a
                href={broker.url}
                target="_blank"
                rel="sponsored nofollow noopener noreferrer"
                aria-label={`Visit ${broker.name} (partner link, opens in a new tab)${
                  isClosestFit ? " — closest fit for this order type" : ""
                }`}
                className="group flex h-full flex-col justify-between gap-3 border-2 border-foreground-950 bg-background-50 px-4 py-3.5 transition-colors hover:bg-foreground-950 cursor-pointer"
              >
                <div>
                  {/* Slot height is reserved on every card so the three brand names
                      stay on one baseline whether or not a card carries the tag. */}
                  <div className="mb-1.5 flex h-[15px] items-center">
                    {isClosestFit && (
                      <span className="inline-flex items-center bg-accent-100 px-1.5 py-[2px] font-label text-[9px] uppercase tracking-[0.14em] text-accent-900 group-hover:bg-accent-500 group-hover:text-foreground-950">
                        Closest fit
                      </span>
                    )}
                  </div>
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-heading font-black uppercase tracking-tight leading-none text-base md:text-lg text-foreground-950 group-hover:text-background-50">
                      {broker.name}
                    </span>
                    <i
                      className="ri-arrow-right-up-line text-base leading-none text-foreground-500 group-hover:text-primary-500"
                      aria-hidden="true"
                    ></i>
                  </div>
                </div>
                <span className="font-label text-[10px] uppercase tracking-[0.14em] text-foreground-500 group-hover:text-background-300">
                  {broker.badge}
                </span>
              </a>
            </li>
            );
          })}
        </ul>
      </div>

      <div className="max-w-7xl mx-auto mt-4">
        <button
          type="button"
          onClick={() => setReasoningOpen((open) => !open)}
          aria-expanded={reasoningOpen}
          aria-controls="broker-selection-rationale"
          className="group inline-flex items-center gap-2 border-2 border-foreground-950 bg-background-50 px-3 py-1.5 transition-colors hover:bg-foreground-950 cursor-pointer"
        >
          <i
            className={`text-[13px] leading-none text-foreground-950 group-hover:text-background-50 ${
              reasoningOpen ? "ri-subtract-line" : "ri-add-line"
            }`}
            aria-hidden="true"
          ></i>
          <span className="font-label text-[10px] uppercase tracking-[0.16em] text-foreground-950 group-hover:text-background-50">
            {selection.title}
          </span>
          <span className="font-label text-[10px] uppercase tracking-[0.16em] text-foreground-500 group-hover:text-background-400">
            {selection.criteria.length} screens
          </span>
        </button>

        <div
          id="broker-selection-rationale"
          aria-hidden={!reasoningOpen}
          className={`grid transition-all duration-300 ease-out ${
            reasoningOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
          }`}
        >
          <div className="overflow-hidden">
            <div className="mt-3 border-2 border-foreground-950 bg-background-50 px-4 py-5 md:px-6 md:py-6">
              <p className="max-w-4xl text-xs md:text-[13px] leading-relaxed text-foreground-700">
                {selection.lead}
              </p>

              <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6">
                <div>
                  <Eyebrow tone="muted">How the list was built</Eyebrow>
                  <ul className="mt-3 flex flex-col gap-3">
                    {selection.criteria.map((criterion, index) => (
                      <li key={criterion.label} className="flex gap-3">
                        <span className="shrink-0 w-6 h-6 flex items-center justify-center border-2 border-foreground-950 bg-accent-100 font-label text-[10px] tracking-[0.06em] text-foreground-950">
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        <div className="min-w-0">
                          <p className="font-heading font-bold uppercase tracking-tight text-xs md:text-[13px] leading-tight text-foreground-950">
                            {criterion.label}
                          </p>
                          <p className="mt-1 text-[11px] md:text-xs leading-relaxed text-foreground-600">
                            {criterion.text}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <Eyebrow tone="muted">What each one is for</Eyebrow>
                  <ul className="mt-3 flex flex-col gap-3">
                    {selection.reasons.map((item) => (
                      <li
                        key={item.name}
                        className="border-b border-background-300/70 pb-3 last:border-b-0 last:pb-0"
                      >
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span className="font-heading font-black uppercase tracking-tight text-xs md:text-[13px] leading-none text-foreground-950">
                            {item.name}
                          </span>
                          <span className="inline-flex items-center bg-secondary-100 px-1.5 py-0.5 font-label text-[9px] uppercase tracking-[0.14em] text-secondary-900">
                            {item.role}
                          </span>
                        </div>
                        <p className="mt-1.5 text-[11px] md:text-xs leading-relaxed text-foreground-600">
                          {item.reason}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-5 flex items-start gap-2 border-t border-background-300/70 pt-3">
                <i
                  className="ri-scales-3-line text-xs text-foreground-500 mt-[2px]"
                  aria-hidden="true"
                ></i>
                <p className="font-label text-[10px] leading-relaxed text-foreground-500">
                  {selection.footnote}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto mt-4 flex items-start gap-2">
        <i className="ri-information-line text-xs text-foreground-500 mt-[2px]" aria-hidden="true"></i>
        <p className="font-label text-[10px] leading-relaxed text-foreground-500">
          Partner links — this site may earn a commission at no extra cost to you. It never changes
          what we write.
        </p>
      </div>
    </section>
  );
}